import {
  ChangeDetectionStrategy,
  Component,
  computed,
  type ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer, type SafeResourceUrl } from '@angular/platform-browser';

import { AuthService } from '@core/auth/services/auth.service';

import { type DynamicAction } from '@shared/registry';

import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-consent-iframe',
  templateUrl: './consent-iframe.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block overflow-hidden flex-1',
    '(window:message)': 'onIframeMessage($event)',
  },
})
export class ConsentIframe {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly authService = inject(AuthService);

  private readonly iframeRef = viewChild<ElementRef<HTMLIFrameElement>>('iframe');

  readonly consentKey = input.required<string>();

  readonly action = output<DynamicAction>();

  private readonly token = signal<string | undefined>(undefined);

  protected readonly iframeUrl = computed<SafeResourceUrl>(() => {
    const key = this.consentKey();
    return this.sanitizer.bypassSecurityTrustResourceUrl(`${environment.consentAppUrl}/consent/${key}`);
  });

  constructor() {
    this.setAuthToken();
  }

  protected onIframeMessage(event: MessageEvent): void {
    const allowedOrigins = [environment.consentAppUrl, window.location.origin];
    if (!allowedOrigins.includes(event.origin)) {
      return;
    }

    if (event.origin === window.location.origin && event.data.type === 'session-refresh') {
      this.setAuthToken();
      this.sendAuthTokenToIframe(true);
      return;
    }

    switch (event.data.type) {
      case 'signed':
        this.onConsentSigned(event.data.documentIds);
        break;
      case 'ready':
        this.sendAuthTokenToIframe();
        break;
    }
  }

  private onConsentSigned(documentIds: string[]): void {
    this.action.emit({ id: 'documentsSigned', data: documentIds });
  }

  private sendAuthTokenToIframe(isRefresh = false): void {
    const iframe = this.iframeRef()?.nativeElement;
    if (!iframe?.contentWindow) return;

    iframe.contentWindow.postMessage(
      {
        type: isRefresh ? 'refresh' : 'auth',
        token: this.token(),
        timestamp: Date.now(),
      },
      environment.consentAppUrl,
    );
  }

  private async setAuthToken(): Promise<void> {
    this.token.set(await this.authService.getToken());
  }
}
